import cloneDeep from 'lodash/cloneDeep';
import store from '@/store/store';
import formatDate from '@/services/general/formatDate';
import { formatDevise, ajoutDevise } from '@/services/general/filters/formatDevise';
import * as devisConf from '@/configuration/devisConf';
import {
  CATEGORIE_DIVERS,
  CATEGORIE_LOCATION,
  CATEGORIE_PRODUIT,
  CATEGORIE_DISPOSITIF,
  CATEGORIE_IMPRESSION_AFFICHE,
} from '@/configuration/catalogue/produitConf';
import gestionDateBase from '@/services/general/gestionDateBase';
import trieDate from '@/services/devisCommandes/trieDate';
import i18n from '@/i18n';
import libelleEcheance from '@/services/devisCommandes/libelleEcheance';
import decimalsFormaterService from '@/services/general/decimalsFormater';
import isNaN from '@/services/general/isNan';
import getDescriptionOfDetailLigne from '@/services/devisCommandes/getDescriptionOfDetailLigne';
import { PARAMETRES_KEYS } from '@/store/modules/parametres-mutation-type';
import { initializeReferenceVentePartielle } from '@/services/devisCommandes/initializeCommandeDevis';
import { MODULE_TLPE } from '@/configuration/gabed/titreRecette/titreRecetteConf';
import { MODULE_ACHAT } from '@/configuration/devisConf';

function getEcheancierLignes(devisCommande, payeur) {
  const lignes = [];
  devisCommande.echeanciers.forEach(echeancier => {
    echeancier.echeancierLignes.forEach(ligne => {
      if (ligne.tiersId === payeur.tiersId) {
        ligne.referenceEcheancier = echeancier.referenceEcheancier;
        ligne.dateDebut = formatDate(ligne.dateDebut);
        ligne.dateFin = formatDate(ligne.dateFin);
        ligne.datePrevisionnelleFacturation = formatDate(ligne.datePrevisionnelleFacturation);
        ligne.montant = formatDevise(ligne.montant);
        lignes.push(ligne);
      }
    });
  });
  return lignes;
}

function decimalsFormater(prix) {
  return ajoutDevise(decimalsFormaterService(Number(prix)));
}

function formatNumber(ligne) {
  ligne.quantite = decimalsFormaterService(ligne.quantite);
  ligne.prixUnitaireNonTransform = ligne.prixUnitaire;
  ligne.prixUnitaire = decimalsFormater(ligne.prixUnitaire);
  ligne.remise = decimalsFormaterService(ligne.remise);
  ligne.tauxTva = !isNaN(Number(ligne.tauxTva))
    ? Number(ligne.tauxTva).toFixed(2) : null;
  ligne.totalHtNonTransform = ligne.totalHt;
  ligne.totalHt = formatDevise(ligne.totalHt);
}

function totalEcheancier(echeancier) {
  let total = 0;
  echeancier.forEach(ligne => {
    total += parseFloat(ligne.montant.replace(/\s/g, ''));
  });
  return formatDevise(total);
}

function getIntervenantLabel(intervenant) {
  if (intervenant.annonceur) {
    return i18n.t('detail.intervenant.annonceur');
  }
  if (intervenant.mandataire) {
    return i18n.t('detail.intervenant.mandataire');
  }
  if (intervenant.donneurOrdre) {
    return i18n.t('detail.intervenant.donneurOrdre');
  }
  return null;
}

function getRemisesInformations(lignesCommande) {
  lignesCommande.forEach(ligne => {
    if (ligne.lignesCommande && ligne.lignesCommande.length) {
      getRemisesInformations(ligne.lignesCommande);
    }
    if (ligne.lignesRemise.length) {
      ligne.lignesRemise.forEach(remise => {
        const typeRemise = devisConf.LISTE_TYPE_REMISE.find(type => {
          return type.code === remise.type;
        });
        if (typeRemise) {
          remise.type = i18n.t(typeRemise.libelle);
        }
        const unit = remise.typeRemise === devisConf.POURCENTAGE ? '%' : '€';
        remise.montant = formatDevise(remise.montant);
        if (unit === '€') {
          remise.remiseLibelle = formatDevise(remise.remise);
        } else {
          remise.remiseLibelle = `${remise.remise} ${unit}`;
        }
      });
    }
  });
}

function initTitleAndRouterLinkFacture(result, devisCommande, isVenteInterne) {
  if (devisCommande.sens === devisConf.SENS_VENTE) {
    result.generalInfos.routerLink = 'detail.commande.referenceCommande';
    result.generalInfos.routerLinkName = isVenteInterne ? 'c.bodac.commandes-venteInterne' : 'c.bodac.commandes';
  }
  if (isVenteInterne && devisCommande.sens === devisConf.SENS_ACHAT) {
    result.generalInfos.routerLink = 'detail.commande.referenceCommandeAchat';
    result.generalInfos.routerLinkName = 'c.bodac.commandes-achatVenteInterne';
  }
  if (devisCommande.sousType === devisConf.SOUS_TYPE_NORMAL
    && devisCommande.type === devisConf.TYPE_FACTURE) {
    if (devisCommande.sens === devisConf.SENS_VENTE || isVenteInterne) {
      result.generalInfos.titleType = i18n.t('detail.facture.facture');
      result.generalInfos.title = i18n.t('detail.titleFacture');
    } else if (devisCommande.module === MODULE_TLPE) {
      result.generalInfos.titleType = i18n.t('detail.facture.titreRecette');
      result.generalInfos.title = i18n.t('detail.titleTitreRecette');
    } else if (devisCommande.module === MODULE_ACHAT) {
      result.generalInfos.titleType = i18n.t('detail.facture.factureAchatFournisseur');
      result.generalInfos.title = i18n.t('detail.titleFactureAchatFournisseur');
    } else {
      result.generalInfos.titleType = i18n.t('detail.facture.factureBailleur');
      result.generalInfos.title = i18n.t('detail.titleFactureBailleur');
    }
    return;
  } if (devisCommande.sousType === devisConf.SOUS_TYPE_AVOIR
      || devisCommande.sousType === devisConf.SOUS_TYPE_AVOIR_SUR_ACOMPTE) {
    if (devisCommande.sens === devisConf.SENS_VENTE || isVenteInterne) {
      result.generalInfos.titleType = i18n.t('detail.facture.factureAvoir');
      result.generalInfos.title = i18n.t('detail.titleAvoir');
    } else if (devisCommande.module === MODULE_TLPE) {
      result.generalInfos.titleType = i18n.t('detail.facture.titreRecetteAvoir');
      result.generalInfos.title = i18n.t('detail.titleAvoirTitreRecette');
    } else if (devisCommande.module === MODULE_ACHAT) {
      result.generalInfos.titleType = i18n.t('detail.facture.factureAvoirAchatFournisseur');
      result.generalInfos.title = i18n.t('detail.titleAvoirAchatFournisseur');
    } else {
      result.generalInfos.titleType = i18n.t('detail.facture.factureAvoirBailleur');
      result.generalInfos.title = i18n.t('detail.titleAvoirBailleur');
    }
    if (devisCommande.reference
      && devisCommande.reference.sousType === devisConf.SOUS_TYPE_ACOMPTE) {
      result.generalInfos.routerLink = 'detail.facture.avoirAcompte';
    } else result.generalInfos.routerLink = 'detail.facture.avoirFacture';
    return;
  }
  if (devisCommande.sens === devisConf.SENS_ACHAT
      && devisCommande.typeReferenceExterne === devisConf.REFERENCE_EXTERNE_CONTRAT) {
    result.generalInfos.routerLink = 'detail.commande.referenceCommande';
    result.generalInfos.routerLinkName = 'c.baux.commande_achat-bailleur';
    result.generalInfos.titleType = i18n.t('detail.commande.commandeAchatTitle');
    result.generalInfos.title = i18n.t('detail.titleCommand');
    return;
  }
  if (result.reference && result.reference.type === devisConf.TYPE_DEVIS) {
    result.generalInfos.routerLink = 'detail.commande.referenceDevis';
    result.generalInfos.routerLinkName = 'c.bodac.devis';
  }
  if (devisCommande.sens === devisConf.SENS_ACHAT
    && devisCommande.type === devisConf.TYPE_COMMANDE) {
    if (devisCommande.sousType === devisConf.SOUS_TYPE_BON_RECEPTION) {
      result.generalInfos.titleType = i18n.t('detail.bonReception.detailTitle');
      result.generalInfos.title = i18n.t('detail.titleBonReception');
    } else {
      result.generalInfos.titleType = i18n.t('detail.commande.commandeAchatTitle');
      result.generalInfos.title = i18n.t('detail.titleCommand');
    }
    return;
  }
  result.generalInfos.titleType = i18n.t('detail.facture.factureAcompte');
  result.generalInfos.title = i18n.t('detail.titleAcompte');
}

function getRepartition(payeur, recapitulatif, intervenants) {
  payeur.montantsRepartition = {};
  const repartitionPourcentage = payeur.typeRepartition === devisConf.POURCENTAGE;
  const tauxTva = (recapitulatif.montantTtc / recapitulatif.montantHt) - 1;
  if (repartitionPourcentage) {
    payeur.montantsRepartition.ht = recapitulatif.montantHt * (payeur.repartition / 100);
  } else {
    payeur.montantsRepartition.ht = payeur.repartition;
  }
  if (repartitionPourcentage) {
    payeur.montantsRepartition.tva = recapitulatif.montantTva * (payeur.repartition / 100);
  } else {
    payeur.montantsRepartition.tva = payeur.repartition * tauxTva;
  }
  if (repartitionPourcentage) {
    payeur.montantsRepartition.ttc = recapitulatif.montantTtc * (payeur.repartition / 100);
  } else {
    payeur.montantsRepartition.ttc = payeur.montantsRepartition.ht + payeur.montantsRepartition.tva;
  }
  payeur.montantsRepartition.ht = formatDevise(payeur.montantsRepartition.ht);
  payeur.montantsRepartition.tva = formatDevise(payeur.montantsRepartition.tva);
  payeur.montantsRepartition.ttc = formatDevise(payeur.montantsRepartition.ttc);

  payeur.annonceurs = intervenants.filter(i => i.annonceur
    && i.tiersId !== payeur.tiersId);
  payeur.donneursOrdre = intervenants.filter(i => !i.annonceur
    && i.donneurOrdre
    && i.tiersId !== payeur.tiersId);
  payeur.intervenants = intervenants.filter(i => !i.annonceur
    && !i.donneurOrdre
    && !i.payeur
    && i.tiersId !== payeur.tiersId);
}

function getCommentaireOfLigne(ligne) {
  return ligne.filter((ligneEnfant) => {
    return ligneEnfant.type === devisConf.COMMENTAIRE;
  });
}

function calculTotalEtHorsTotal(ligne, dcf, allCategories) {
  if (ligne.type === devisConf.NORMAL) {
    if (ligne.produit
      && ligne.produit.categorieGroupeProduitDeductibleId) {
      const catgorieFind = dcf.categorieGroupeProduitInDcf.find((cat) => {
        return cat.id === ligne.produit.categorieGroupeProduitDeductibleId;
      });
      if (catgorieFind) {
        catgorieFind.total += ligne.totalHtAvecLigneRemise;
      } else {
        const catFindInAllCategories = allCategories.find((cat) => {
          return cat.id === ligne.produit.categorieGroupeProduitDeductibleId;
        });
        if (!catFindInAllCategories) return;
        dcf.categorieGroupeProduitInDcf.push({
          id: ligne.produit.categorieGroupeProduitDeductibleId,
          libelle: catFindInAllCategories.libelle,
          total: ligne.totalHtAvecLigneRemise,
        });
      }
    } else dcf.totalHors += ligne.totalHtAvecLigneRemise;
  }
}

export default function detailDevisCommande(devisCommandeBase, isVenteInterne = false) {
  const devisCommande = cloneDeep(devisCommandeBase);
  if (!Object.prototype.hasOwnProperty.call(devisCommande, 'echeanciers')) {
    devisCommande.echeanciers = [];
  }
  if (!Object.prototype.hasOwnProperty.call(devisCommande, 'acomptesAffectes')) {
    devisCommande.acomptesAffectes = [];
  }
  if (!Object.prototype.hasOwnProperty.call(devisCommande, 'echeancierPaiement')) {
    devisCommande.echeancierPaiement = [];
  }
  if (!Object.prototype.hasOwnProperty.call(devisCommande, 'signaturesHisto')) {
    devisCommande.signaturesHisto = [];
  }
  const result = {};
  result.code = devisCommande.code;
  result.id = devisCommande.id;
  devisCommande.dateDevisCommande = gestionDateBase(devisCommande.dateDevisCommande);
  devisCommande.dateValidite = gestionDateBase(devisCommande.dateValidite);
  devisCommande.datePremierAffichage = gestionDateBase(devisCommande.datePremierAffichage);
  if (devisCommandeBase.planMediaId) {
    devisCommande.planMedia = devisCommandeBase.planMedia;
    devisCommande.planMediaId = devisCommandeBase.planMediaId;
  }
  if (devisCommandeBase.campagneId) {
    devisCommande.campagne = devisCommandeBase.campagne;
    devisCommande.campagneId = devisCommandeBase.campagneId;
  }
  initializeReferenceVentePartielle(devisCommande);
  result.generalInfos = {
    dateDevisCommande: formatDate(devisCommande.dateDevisCommande),
    dateValidite: null,
    theme: devisCommande.theme,
    codeDossier: devisCommande.codeDossier,
    referenceClient: devisCommande.referenceClient,
    agenceId: devisCommande.agenceId,
    societeId: devisCommande.societeId,
    role: devisCommande.role,
    estSigne: devisCommande.estSigne,
    commission: devisCommande.commissionCommercial,
    referenceExterne: devisCommande.referenceExterne,
    typeReferenceExterne: devisCommande.typeReferenceExterne,
    planMediaCode: devisCommande.planMedia?.code ?? null,
    planMediaId: devisCommande.planMediaId ?? null,
    campagneLibelle: devisCommande.campagne?.libelle ?? null,
    campagneId: devisCommande.campagneId ?? null,
  };
  if (devisCommande.dateValidite) {
    result.generalInfos.dateValidite = formatDate(devisCommande.dateValidite);
  }
  if (devisCommande.datePremierAffichage) {
    result.generalInfos.datePremierAffichage = formatDate(devisCommande.datePremierAffichage);
  }
  result.generalInfos.categorieVente = CATEGORIE_PRODUIT.find((categorie) => {
    return categorie.code === devisCommande.categorieVente;
  }).libelle;
  result.generalInfos.commercial = devisCommande.commercial;
  result.generalInfos.assistant = devisCommande.assistant;
  result.etat = devisCommande.etat;
  result.sousType = devisCommande.sousType;
  result.payeurs = [];
  result.intervenants = [];
  result.signaturesHisto = devisCommande.signaturesHisto;
  result.signaturesHisto.forEach(signature => {
    signature.dateEnvoie = gestionDateBase(signature.dateEnvoie);
    signature.dateSignature = gestionDateBase(signature.dateSignature);
  });
  result.destinataireComptable = devisCommande.intervenants.find((intervenant) => {
    return intervenant.destinataireComptable;
  });
  result.destinataire = devisCommande.intervenants.find(i => i.destinataire);
  devisCommande.intervenants.forEach(intervenant => {
    intervenant.libelle = getIntervenantLabel(intervenant);
    if (result.destinataire && result.destinataire.tiersId === intervenant.tiersId) {
      return;
    }
    if (intervenant.payeur && !result.destinataire) {
      result.payeurs.push(intervenant);
      return;
    }
    result.intervenants.push(intervenant);
  });

  result.payeurs.forEach(payeur => {
    payeur.echeancier = getEcheancierLignes(devisCommande, payeur);
    payeur.echeancier.forEach(ligne => {
      ligne.libelle = libelleEcheance(devisCommande.lignesCommande, ligne.referenceEcheancier);
    });
    payeur.echeancierInfos = {
      total: totalEcheancier(payeur.echeancier),
      modeReglement: payeur.modeReglement ? payeur.modeReglement.libelle : null,
    };
  });
  result.commentaireInterne = devisCommande.commentaireInterne;
  result.adresseLivraison = devisCommande.adresseLivraison;
  result.referenceClient = devisCommande.referenceClient;
  result.referenceDevis = devisCommande.referenceDevis;
  result.lignesCommande = devisCommande.lignesCommande;
  result.totalHors = 0;
  result.categorieGroupeProduitInDcf = [];
  getRemisesInformations(result.lignesCommande, devisCommande.type, devisCommande.sousType);
  result.reference = devisCommande.reference;
  if (result.reference) {
    result.reference.dateDevisCommande = formatDate(result.reference.dateDevisCommande);
  }
  const allCategorieGroupeProduitDeductible = store.getters['parametres/parametre'](PARAMETRES_KEYS.categorieGroupeProduitDeductible);
  result.lignesCommande.forEach(ligne => {
    ligne.selectedLogistique = false;
    ligne.demandesLogistique = [];
    ligne.dateLivraison = gestionDateBase(ligne.dateLivraison);
    ligne.dateBatDemande = gestionDateBase(ligne.dateBatDemande);
    ligne.dateBatCreation = gestionDateBase(ligne.dateBatCreation);
    ligne.dateBatModification = gestionDateBase(ligne.dateBatModification);
    ligne.dateBatFormat = gestionDateBase(ligne.dateBatFormat);
    formatNumber(ligne);
    calculTotalEtHorsTotal(ligne, result, allCategorieGroupeProduitDeductible);
    ligne.remise += ligne.typeRemise === devisConf.MONTANT ? ' €' : ' %';
    if (ligne.lignesCommande) {
      let isFirstLigne = true;
      ligne.lignesCommande.forEach(ligneEnfant => {
        calculTotalEtHorsTotal(ligneEnfant, result, allCategorieGroupeProduitDeductible);
        formatNumber(ligneEnfant);
        ligneEnfant.remise += ligneEnfant.typeRemise === devisConf.MONTANT ? ' €' : ' %';
        ligneEnfant.description = getDescriptionOfDetailLigne(ligneEnfant,
          ligne, devisCommandeBase.type, getCommentaireOfLigne(ligne.lignesCommande), isFirstLigne);
        isFirstLigne = false;
      });
    }
    if (ligne.typeProduit === CATEGORIE_DISPOSITIF || (devisCommande.sens === devisConf.SENS_ACHAT
        && (ligne.typeProduit === CATEGORIE_LOCATION || ligne.typeProduit === CATEGORIE_DIVERS
          || !ligne.typeProduit))) {
      ligne.description = getDescriptionOfDetailLigne(null,
        ligne, devisCommandeBase.type, [], true);
      if (ligne.ligneCommandeDispositif?.nomenclatureTypeId) {
        ligne.ligneCommandeDispositif.nomenclature = JSON.parse(
          ligne.ligneCommandeDispositif.nomenclature);
      }
    }
    if (ligne.typeProduit === CATEGORIE_IMPRESSION_AFFICHE
      && devisCommande.sens === devisConf.SENS_ACHAT
      && (devisCommande.type === devisConf.TYPE_COMMANDE
        || devisCommande.type === devisConf.TYPE_FACTURE)) {
      ligne.description = getDescriptionOfDetailLigne(null,
        ligne, devisCommandeBase.type, [], true);
    }
  });
  result.modeReglement = devisCommande.modeReglement;
  result.modeReglementId = devisCommande.modeReglementId;
  result.acomptesAffectes = devisCommande.acomptesAffectes;
  result.acomptesAffectes.forEach((facture) => {
    facture.montant = formatDevise(facture.montantTtc);
    facture.date = formatDate(facture.dateDevisCommande);
  });
  result.echeancierPaiement = devisCommande.echeancierPaiement;
  trieDate(result.echeancierPaiement);
  result.repartitionsTva = devisCommande.repartitionsTva;
  result.repartitionsTva.forEach(item => {
    item.totalHt = formatDevise(item.totalHt);
    item.tauxTva = item.tauxTva.toFixed(2);
    item.totalTva = formatDevise(item.totalTva);
  });
  result.recapitulatifMontants = {
    total: result.categorieGroupeProduitInDcf,
    totalHors: result.categorieGroupeProduitInDcf.length > 0
      ? decimalsFormater(result.totalHors) : null,
    montantHt: devisCommande.montantHt,
    montantHtFormated: formatDevise(devisCommande.montantHt),
    montantTva: devisCommande.montantTva,
    montantTvaFormated: formatDevise(devisCommande.montantTva),
    montantTtc: devisCommande.montantTtc,
    montantTtcFormated: formatDevise(devisCommande.montantTtc),
    montantAcomptesAffectes: devisCommande.type === devisConf.TYPE_FACTURE
      ? devisCommande.montantAcomptesAffectes
      : devisCommande.acomptesAffectes
        .filter(a => a.etat !== devisConf.ETAT_ANNULER)
        .reduce((acc, curr) => {
          const cumul = acc + curr.montantTtc;
          return cumul;
        }, 0),
  };
  result.dateEmission = formatDate(devisCommande.creationTime);
  result.penalite = devisCommande.penalite;
  result.tauxPenalite = devisCommande.tauxPenalite;
  result.escompte = devisCommande.escompte;
  result.tauxEscompte = devisCommande.tauxEscompte;
  result.recouvrement = devisCommande.recouvrement;
  result.commentaire = devisCommande.commentaire;
  if (result.tauxPenalite) {
    result.tauxPenalite = result.tauxPenalite.toFixed(2);
  }
  if (result.tauxEscompte) {
    result.tauxEscompte = result.tauxEscompte.toFixed(2);
  }
  result.etatRenouvellement = devisCommande.etatRenouvellement;
  result.reconductible = devisCommande.reconductible;
  result.renouvelable = devisCommande.renouvelable;
  result.payeurs.forEach(payeur => {
    getRepartition(payeur, result.recapitulatifMontants, devisCommande.intervenants);
  });
  result.tiersFactorId = devisCommande.tiersFactorId ? devisCommande.tiersFactorId : null;
  result.tiersFactor = devisCommande.tiersFactorId ? devisCommande.tiersFactor : null;
  result.factorTransmis = devisCommande.factorTransmis;
  result.factorDateTransmission = formatDate(devisCommande.factorDateTransmission);
  result.commentaireFactor = devisCommande.commentaireFactor;
  result.codeDossier = devisCommande.codeDossier;
  initTitleAndRouterLinkFacture(result, devisCommande, isVenteInterne);
  return result;
}
